/**
 * Calculate the duration in years between two dates
 * @param startDate Start date in ISO format (YYYY-MM-DD)
 * @param endDate End date in ISO format (YYYY-MM-DD), null for current positions
 * @returns Number of years (with decimals)
 */
export const calculateYearsOfExperience = (
  startDate: string | null,
  endDate: string | null
): number => {
  if (!startDate) return 0;

  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date(); // Use current date if end_date is null

  // Calculate difference in milliseconds
  const diffMs = end.getTime() - start.getTime();
  
  // Convert to years (365.25 days per year to account for leap years)
  const years = diffMs / (1000 * 60 * 60 * 24 * 365.25);
  
  // Return minimum of 0.1 years (about 1 month) for very short experiences
  return Math.max(years, 0.1);
};

/**
 * Format duration as a readable string
 * @param startDate Start date in ISO format (YYYY-MM-DD)
 * @param endDate End date in ISO format (YYYY-MM-DD), null for current positions
 * @returns Formatted duration string (e.g., "2.5 years", "< 1 year")
 */
export const formatDuration = (
  startDate: string | null,
  endDate: string | null
): string => {
  const years = calculateYearsOfExperience(startDate, endDate);
  
  if (years < 1) {
    return "< 1 year";
  } else if (years === 1) {
    return "1 year";
  } else {
    return `${Math.round(years)} years`;
  }
};

/**
 * Format date range as a readable string
 * @param startDate Start date in ISO format (YYYY-MM-DD)
 * @param endDate End date in ISO format (YYYY-MM-DD), null for current positions
 * @returns Formatted date range (e.g., "Jan 2020 - Present", "Mar 2018 - Dec 2019")
 */
export const formatDateRange = (
  startDate: string | null,
  endDate: string | null
): string => {
  if (!startDate) return "";

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} ${year}`;
  };

  const start = formatDate(startDate);
  const end = endDate ? formatDate(endDate) : "Present";

  return `${start} - ${end}`;
};

/**
 * Get the year from a date string
 * @param dateStr Date string in ISO format (YYYY-MM-DD)
 * @returns Year as number
 */
export const getYear = (dateStr: string | null): number => {
  if (!dateStr) return new Date().getFullYear();
  return new Date(dateStr).getFullYear();
};

